import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuthSession } from "../useAuthSession";
import {
  PermissionResult,
  Resource,
  PermissionAction,
  AttributeContext,
  PermissionCheck,
  PermissionState,
} from "../../types/permissions";
import { permissionService } from "../../services/permissions/permissionService";
import { logger } from "../../utils/logger";

interface UsePermissionsOptions {
  resources?: Resource[];
  actions?: PermissionAction[];
  cacheTime?: number;
  staleTime?: number;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
}

interface UsePermissionsResult {
  permissions: Record<string, PermissionResult>;
  loading: boolean;
  error: string | null;
  hasPermission: (resource: Resource, action: PermissionAction) => boolean;
  checkPermission: (
    resource: Resource,
    action: PermissionAction,
    context?: Partial<AttributeContext>,
  ) => Promise<PermissionResult>;
  checkBulkPermissions: (
    checks: PermissionCheck[],
  ) => Promise<Record<string, PermissionResult>>;
  getPermissionReason: (
    resource: Resource,
    action: PermissionAction,
  ) => string | undefined;
  refreshPermissions: (resourceIds?: string[]) => Promise<void>;
  invalidateCache: (pattern?: string) => void;
  isStale: boolean;
}

export function usePermissions(
  options: UsePermissionsOptions = {},
): UsePermissionsResult {
  const { user, isAuthenticated } = useAuthSession();
  const [state, setState] = useState<PermissionState>({
    permissions: {},
    loading: false,
    error: null,
    lastUpdated: undefined,
  });

  const [isStale, setIsStale] = useState(false);
  const abortController = useRef<AbortController | null>(null);
  const cacheTime = options.cacheTime ?? 5 * 60 * 1000; // 5 minutes
  const staleTime = options.staleTime ?? 30 * 1000; // 30 seconds

  // Generate cache keys for resources and actions
  const generateCacheKey = useCallback(
    (resource: Resource, action: PermissionAction): string => {
      return `${resource.type}:${resource.id}:${action}`;
    },
    [],
  );

  // Check if permission is cached and not stale
  const isCached = useCallback(
    (resource: Resource, action: PermissionAction): boolean => {
      const key = generateCacheKey(resource, action);
      const permission = state.permissions[key];
      if (!permission) return false;

      const now = Date.now();
      const age = now - permission.evaluatedAt.getTime();
      return age < cacheTime;
    },
    [state.permissions, cacheTime, generateCacheKey],
  );

  // Check if cache is stale
  const isCacheStale = useCallback((): boolean => {
    if (!state.lastUpdated) return true;
    const now = Date.now();
    const age = now - state.lastUpdated.getTime();
    return age > staleTime;
  }, [state.lastUpdated, staleTime]);

  // Single permission check with caching
  const checkPermission = useCallback(
    async (
      resource: Resource,
      action: PermissionAction,
      context?: Partial<AttributeContext>,
    ): Promise<PermissionResult> => {
      if (!isAuthenticated || !user) {
        return {
          allowed: false,
          reason: "User not authenticated",
          evaluatedAt: new Date(),
          cached: false,
        };
      }

      const key = generateCacheKey(resource, action);

      // Return cached result if valid
      if (isCached(resource, action)) {
        const cached = state.permissions[key];
        logger.debug("Permission cache hit", {
          key,
          resource: resource.type,
          action,
        });
        return { ...cached, cached: true };
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const result = await permissionService.checkPermission({
          resource,
          action,
          context,
          userId: user.id,
        });

        // Update cache
        setState((prev) => ({
          ...prev,
          permissions: {
            ...prev.permissions,
            [key]: { ...result, cached: false },
          },
          loading: false,
          lastUpdated: new Date(),
        }));

        logger.debug("Permission checked", {
          key,
          resource: resource.type,
          action,
          allowed: result.allowed,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Permission check failed";
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));

        logger.error("Permission check error", {
          key,
          resource: resource.type,
          action,
          error,
        });

        // Return deny by default on error
        return {
          allowed: false,
          reason: errorMessage,
          evaluatedAt: new Date(),
          cached: false,
        };
      }
    },
    [isAuthenticated, user, generateCacheKey, isCached],
  );

  // Bulk permission check
  const checkBulkPermissions = useCallback(
    async (
      checks: PermissionCheck[],
    ): Promise<Record<string, PermissionResult>> => {
      if (!isAuthenticated || !user) {
        return checks.reduce(
          (acc, check) => ({
            ...acc,
            [check.id]: {
              allowed: false,
              reason: "User not authenticated",
              evaluatedAt: new Date(),
              cached: false,
            },
          }),
          {},
        );
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const results = await permissionService.checkBulkPermissions({
          checks,
          userId: user.id,
        });

        // Update cache with all results
        const newPermissions = { ...state.permissions };
        Object.entries(results).forEach(([checkId, result]) => {
          const check = checks.find((c) => c.id === checkId);
          if (check) {
            const key = generateCacheKey(check.resource, check.action);
            newPermissions[key] = result;
          }
        });

        setState((prev) => ({
          ...prev,
          permissions: newPermissions,
          loading: false,
          lastUpdated: new Date(),
        }));

        logger.debug("Bulk permissions checked", {
          count: checks.length,
          results: Object.keys(results).length,
        });

        return results;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Bulk permission check failed";
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));

        logger.error("Bulk permission check error", {
          checksCount: checks.length,
          error,
        });

        // Return deny by default for all checks
        return checks.reduce(
          (acc, check) => ({
            ...acc,
            [check.id]: {
              allowed: false,
              reason: errorMessage,
              evaluatedAt: new Date(),
              cached: false,
            },
          }),
          {},
        );
      }
    },
    [isAuthenticated, user, generateCacheKey, state.permissions],
  );

  // Quick synchronous permission check (uses cache only)
  const hasPermission = useCallback(
    (resource: Resource, action: PermissionAction): boolean => {
      if (!isAuthenticated || !user) return false;

      const key = generateCacheKey(resource, action);
      const permission = state.permissions[key];

      if (!permission) {
        // If not cached, trigger async check but return false for now
        checkPermission(resource, action).catch((error) => {
          logger.warn("Background permission check failed", { error });
        });
        return false;
      }

      // Check if cache is expired
      const now = Date.now();
      const age = now - permission.evaluatedAt.getTime();
      if (age > cacheTime) {
        // Trigger refresh in background but return cached result
        checkPermission(resource, action).catch((error) => {
          logger.warn("Background permission refresh failed", { error });
        });
      }

      return permission.allowed;
    },
    [
      isAuthenticated,
      user,
      generateCacheKey,
      state.permissions,
      cacheTime,
      checkPermission,
    ],
  );

  // Get permission denial reason
  const getPermissionReason = useCallback(
    (resource: Resource, action: PermissionAction): string | undefined => {
      const key = generateCacheKey(resource, action);
      return state.permissions[key]?.reason;
    },
    [generateCacheKey, state.permissions],
  );

  // Refresh permissions for specific resources or all
  const refreshPermissions = useCallback(
    async (resourceIds?: string[]): Promise<void> => {
      if (!isAuthenticated || !user) return;

      // Cancel any ongoing request
      if (abortController.current) {
        abortController.current.abort();
      }

      abortController.current = new AbortController();

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        let permissionsToRefresh: string[] = [];

        if (resourceIds) {
          // Refresh specific resources
          permissionsToRefresh = Object.keys(state.permissions).filter(
            (key) => {
              const [resourceType, resourceId] = key.split(":");
              return resourceIds.includes(resourceId);
            },
          );
        } else {
          // Refresh all cached permissions
          permissionsToRefresh = Object.keys(state.permissions);
        }

        // Build checks for refresh
        const checks: PermissionCheck[] = permissionsToRefresh.map((key) => {
          const [resourceType, resourceId, action] = key.split(":");
          return {
            id: key,
            resource: { id: resourceId, type: resourceType as any },
            action: action as PermissionAction,
          };
        });

        if (checks.length > 0) {
          const results = await permissionService.checkBulkPermissions({
            checks,
            userId: user.id,
          });

          // Update cache
          const newPermissions = { ...state.permissions };
          Object.entries(results).forEach(([checkId, result]) => {
            newPermissions[checkId] = result;
          });

          setState((prev) => ({
            ...prev,
            permissions: newPermissions,
            loading: false,
            lastUpdated: new Date(),
          }));

          logger.info("Permissions refreshed", { count: checks.length });
        } else {
          setState((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        if ((error as any)?.name !== "AbortError") {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Permission refresh failed";
          setState((prev) => ({
            ...prev,
            loading: false,
            error: errorMessage,
          }));
          logger.error("Permission refresh error", { error });
        }
      }
    },
    [isAuthenticated, user, state.permissions],
  );

  // Invalidate cache for specific pattern or all
  const invalidateCache = useCallback(
    (pattern?: string): void => {
      if (pattern) {
        // Remove permissions matching pattern (simple string includes for now)
        const newPermissions = Object.entries(state.permissions).reduce(
          (acc, [key, value]) => {
            if (!key.includes(pattern)) {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, PermissionResult>,
        );

        setState((prev) => ({
          ...prev,
          permissions: newPermissions,
        }));

        logger.debug("Permission cache invalidated", { pattern });
      } else {
        // Clear all cache
        setState((prev) => ({
          ...prev,
          permissions: {},
          lastUpdated: undefined,
        }));

        logger.debug("Permission cache cleared");
      }
    },
    [state.permissions],
  );

  // Initial load of permissions if resources/actions provided
  useEffect(() => {
    if (
      options.resources &&
      options.actions &&
      options.refetchOnMount !== false
    ) {
      const checks: PermissionCheck[] = [];

      options.resources.forEach((resource) => {
        options.actions!.forEach((action) => {
          checks.push({
            id: generateCacheKey(resource, action),
            resource,
            action,
          });
        });
      });

      if (checks.length > 0) {
        checkBulkPermissions(checks).catch((error) => {
          logger.warn("Initial permission load failed", { error });
        });
      }
    }
  }, [
    options.resources,
    options.actions,
    options.refetchOnMount,
    generateCacheKey,
    checkBulkPermissions,
  ]);

  // Check if cache is stale
  useEffect(() => {
    setIsStale(isCacheStale());

    const interval = setInterval(() => {
      setIsStale(isCacheStale());
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [isCacheStale]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return {
    permissions: state.permissions,
    loading: state.loading,
    error: state.error,
    hasPermission,
    checkPermission,
    checkBulkPermissions,
    getPermissionReason,
    refreshPermissions,
    invalidateCache,
    isStale,
  };
}

export default usePermissions;

// Additional hook for permission matrix management
export const usePermissionMatrix = () => {
  const [isBulkUpdating, setIsBulkUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchMatrix = React.useCallback(
    async (params?: {
      projectId?: string;
      organizationId?: string;
      scope?: string;
    }) => {
      try {
        // TODO: Implement actual API call
        console.log("Fetching permission matrix with params:", params);
        return {
          success: true,
          data: [],
        };
      } catch (error) {
        console.error("Failed to fetch permission matrix:", error);
        setError(
          error instanceof Error ? error.message : "Failed to fetch matrix",
        );
        return {
          success: false,
          error:
            error instanceof Error ? error.message : "Failed to fetch matrix",
        };
      }
    },
    [],
  );

  const bulkUpdate = React.useCallback(async (updates: any[]) => {
    try {
      setIsBulkUpdating(true);
      setError(null);

      // TODO: Implement actual API call
      console.log("Bulk updating permissions:", updates);

      return {
        success: true,
        updatedCount: updates.length,
      };
    } catch (error) {
      console.error("Failed to bulk update permissions:", error);
      setError(error instanceof Error ? error.message : "Bulk update failed");
      return {
        success: false,
        error: error instanceof Error ? error.message : "Bulk update failed",
      };
    } finally {
      setIsBulkUpdating(false);
    }
  }, []);

  const exportMatrix = React.useCallback(
    async (
      format: "json" | "csv" = "json",
      params?: {
        projectId?: string;
        organizationId?: string;
      },
    ) => {
      try {
        // TODO: Implement actual API call
        console.log("Exporting permission matrix:", format, params);

        const mockData =
          format === "json"
            ? JSON.stringify(
                { matrix: [], exportedAt: new Date().toISOString() },
                null,
                2,
              )
            : "Role,Read,Write,Delete\nAdmin,Yes,Yes,Yes\nUser,Yes,No,No";

        return {
          success: true,
          data: mockData,
          filename: `permissions-${Date.now()}.${format}`,
        };
      } catch (error) {
        console.error("Failed to export permission matrix:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Export failed",
        };
      }
    },
    [],
  );

  return {
    fetchMatrix,
    bulkUpdate,
    exportMatrix,
    isBulkUpdating,
    error,
  };
};
